import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAlert } from '../hooks/useAlert';
import Header from '../components/Header';
import CustomAlert from '../components/CustomAlert';
import './Speakers.css';

function Speakers() {
  const [speakers, setSpeakers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('lastSeen');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('');
  const [mergingSpeaker, setMergingSpeaker] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [viewingAppearances, setViewingAppearances] = useState(null);
  const [appearances, setAppearances] = useState([]);
  const [isLoadingAppearances, setIsLoadingAppearances] = useState(false);

  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { alert, showConfirm, showSuccess, showError } = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    fetchSpeakers();
    fetchStatistics();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchSpeakers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/speakers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSpeakers(data);
      } else {
        showError('Error', 'Failed to load speakers');
      }
    } catch (err) {
      console.error('Error fetching speakers:', err);
      showError('Error', `Failed to load speakers: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/speakers/statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      console.error('Error fetching speaker statistics:', err);
    }
  };

  const handleEditSpeaker = (speaker) => {
    setEditingSpeaker(speaker);
    setEditingName(speaker.name);
    setEditingColor(speaker.color || '#4CAF50');
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim()) {
      showError('Error', 'Speaker name cannot be empty');
      return;
    }

    try {
      const response = await fetch(`/api/speakers/${editingSpeaker.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingName.trim(),
          color: editingColor
        })
      });

      if (response.ok) {
        showSuccess('Success', 'Speaker updated successfully');
        setEditingSpeaker(null);
        fetchSpeakers();
        fetchStatistics();
      } else {
        const errorData = await response.json();
        showError('Error', errorData.error || 'Failed to update speaker');
      }
    } catch (err) {
      console.error('Error updating speaker:', err);
      showError('Error', `Failed to update speaker: ${err.message}`);
    }
  };

  const handleDeleteSpeaker = async (speaker) => {
    const confirmed = await showConfirm(
      'Delete Speaker',
      `Are you sure you want to delete "${speaker.name}"? This will unlink them from ${speaker.totalAppearances} transcript(s).`,
      { confirmText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/speakers/${speaker.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        showSuccess('Success', 'Speaker deleted successfully');
        fetchSpeakers();
        fetchStatistics();
      } else {
        const errorData = await response.json();
        showError('Error', errorData.error || 'Failed to delete speaker');
      }
    } catch (err) {
      console.error('Error deleting speaker:', err);
      showError('Error', `Failed to delete speaker: ${err.message}`);
    }
  };

  const handleViewAppearances = async (speaker) => {
    setViewingAppearances(speaker);
    setIsLoadingAppearances(true);
    setAppearances([]);

    try {
      const response = await fetch(`/api/speakers/${speaker.id}/appearances`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAppearances(data.appearances || []);
      } else {
        showError('Error', 'Failed to load speaker appearances');
      }
    } catch (err) {
      console.error('Error fetching appearances:', err);
      showError('Error', `Failed to load appearances: ${err.message}`);
    } finally {
      setIsLoadingAppearances(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMergeSpeakers = async () => {
    if (!mergeTargetId) {
      showError('Error', 'Please select a target speaker to merge into');
      return;
    }

    if (mergingSpeaker.id === mergeTargetId) {
      showError('Error', 'Cannot merge a speaker into itself');
      return;
    }

    const targetSpeaker = speakers.find(s => s.id === mergeTargetId);
    const confirmed = await showConfirm(
      'Merge Speakers',
      `Merge "${mergingSpeaker.name}" into "${targetSpeaker.name}"? All transcript appearances will be transferred.`,
      { confirmText: 'Merge', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/speakers/${mergingSpeaker.id}/merge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetSpeakerId: mergeTargetId })
      });

      if (response.ok) {
        showSuccess('Success', `Merged "${mergingSpeaker.name}" into "${targetSpeaker.name}"`);
        setMergingSpeaker(null);
        setMergeTargetId('');
        fetchSpeakers();
        fetchStatistics();
      } else {
        const errorData = await response.json();
        showError('Error', errorData.error || 'Failed to merge speakers');
      }
    } catch (err) {
      console.error('Error merging speakers:', err);
      showError('Error', `Failed to merge speakers: ${err.message}`);
    }
  };

  // Filter and sort speakers
  const filteredSpeakers = speakers.filter(speaker =>
    speaker.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedSpeakers = [...filteredSpeakers].sort((a, b) => {
    let compareA, compareB;

    switch (sortBy) {
      case 'name':
        compareA = a.name.toLowerCase();
        compareB = b.name.toLowerCase();
        break;
      case 'appearances':
        compareA = a.totalAppearances;
        compareB = b.totalAppearances;
        break;
      case 'lastSeen':
      default:
        compareA = new Date(a.lastSeen).getTime();
        compareB = new Date(b.lastSeen).getTime();
        break;
    }

    if (sortOrder === 'asc') {
      return compareA > compareB ? 1 : -1;
    } else {
      return compareA < compareB ? 1 : -1;
    }
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  return (
    <div className="speakers-page">
      <Header
        title="Speaker Management"
        subtitle="Manage speaker profiles and voice identification"
      />

      <div className="speakers-content">
        {/* Statistics Cards */}
        {statistics && (
          <div className="speaker-stats-cards">
            <div className="stat-card">
              <div className="stat-value">{statistics.totalSpeakers}</div>
              <div className="stat-label">Total Speakers</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{statistics.speakersWithEmbeddings}</div>
              <div className="stat-label">With Voice Profiles</div>
            </div>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="speakers-toolbar">
          <input
            type="text"
            placeholder="Search speakers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="speaker-search-input"
          />

          <div className="speakers-sort-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="lastSeen">Last Seen</option>
              <option value="appearances">Appearances</option>
              <option value="name">Name</option>
            </select>

            <button
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="sort-order-button"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Speakers List */}
        {isLoading ? (
          <div className="speakers-loading">Loading speakers...</div>
        ) : sortedSpeakers.length === 0 ? (
          <div className="speakers-empty">
            {searchQuery ?
              `No speakers found matching "${searchQuery}"` :
              'No speakers yet. Speakers will appear here after you transcribe audio with diarization enabled.'
            }
          </div>
        ) : (
          <div className="speakers-grid">
            {sortedSpeakers.map(speaker => (
              <div key={speaker.id} className="speaker-card">
                <div className="speaker-card-header">
                  <div className="speaker-color-badge" style={{ backgroundColor: speaker.color }} />
                  <div className="speaker-header-info">
                    <h3 className="speaker-name">{speaker.name}</h3>
                    <span className="speaker-appearances-badge">
                      {speaker.totalAppearances} {speaker.totalAppearances === 1 ? 'transcript' : 'transcripts'}
                    </span>
                  </div>
                </div>

                <div className="speaker-card-stats">
                  <div className="speaker-stat">
                    <span className="speaker-stat-label">Last seen:</span>
                    <span className="speaker-stat-value">{formatDate(speaker.lastSeen)}</span>
                  </div>
                  <div className="speaker-stat">
                    <span className="speaker-stat-label">First seen:</span>
                    <span className="speaker-stat-value">{formatDate(speaker.firstSeen)}</span>
                  </div>
                  <div className="speaker-stat">
                    <span className="speaker-stat-label">Voice profile:</span>
                    <span className="speaker-stat-value">{speaker.embedding ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <div className="speaker-card-actions">
                  <button
                    onClick={() => handleViewAppearances(speaker)}
                    className="speaker-action-button view-button"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleEditSpeaker(speaker)}
                    className="speaker-action-button edit-button"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setMergingSpeaker(speaker)}
                    className="speaker-action-button merge-button"
                  >
                    Merge
                  </button>
                  <button
                    onClick={() => handleDeleteSpeaker(speaker)}
                    className="speaker-action-button delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Speaker Modal */}
      {editingSpeaker && (
        <div className="modal-overlay" onClick={() => setEditingSpeaker(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Speaker</h2>
            <div className="modal-form">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="Speaker name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-picker-group">
                  <input
                    type="color"
                    value={editingColor}
                    onChange={(e) => setEditingColor(e.target.value)}
                  />
                  <span className="color-preview" style={{ backgroundColor: editingColor }}>
                    {editingColor}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setEditingSpeaker(null)} className="cancel-button">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="confirm-button">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Speaker Modal */}
      {mergingSpeaker && (
        <div className="modal-overlay" onClick={() => setMergingSpeaker(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Merge Speaker</h2>
            <p className="modal-description">
              Merge "{mergingSpeaker.name}" into another speaker. All transcript appearances will be transferred.
            </p>
            <div className="modal-form">
              <div className="form-group">
                <label>Merge into</label>
                <select
                  value={mergeTargetId}
                  onChange={(e) => setMergeTargetId(e.target.value)}
                  className="merge-target-select"
                >
                  <option value="">Select target speaker...</option>
                  {speakers
                    .filter(s => s.id !== mergingSpeaker.id)
                    .map(speaker => (
                      <option key={speaker.id} value={speaker.id}>
                        {speaker.name} ({speaker.totalAppearances} appearances)
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setMergingSpeaker(null)} className="cancel-button">
                Cancel
              </button>
              <button onClick={handleMergeSpeakers} className="confirm-button">
                Merge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Appearances Modal */}
      {viewingAppearances && (
        <div className="modal-overlay" onClick={() => setViewingAppearances(null)}>
          <div className="modal-content appearances-modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              <span className="speaker-color-dot-inline" style={{ backgroundColor: viewingAppearances.color }} />
              {viewingAppearances.name}
            </h2>
            <p className="modal-description">
              Appears in {viewingAppearances.totalAppearances} {viewingAppearances.totalAppearances === 1 ? 'transcript' : 'transcripts'}
            </p>

            {isLoadingAppearances ? (
              <div className="appearances-loading">Loading appearances...</div>
            ) : appearances.length === 0 ? (
              <div className="appearances-empty">No transcript appearances found.</div>
            ) : (
              <div className="appearances-list">
                {appearances.map(appearance => (
                  <div
                    key={`${appearance.transcriptId}-${appearance.localLabel}`}
                    className="appearance-item"
                    onClick={() => navigate(`/?transcript=${appearance.transcriptId}`)}
                  >
                    <div className="appearance-info">
                      <span className="appearance-name">{appearance.transcriptName || 'Untitled'}</span>
                      <span className="appearance-date">{formatDate(appearance.transcriptCreatedAt)}</span>
                    </div>
                    <div className="appearance-stats">
                      <span className="appearance-stat" title="Speaking time">
                        {formatDuration(appearance.speakingTime)}
                      </span>
                      <span className="appearance-stat" title="Word count">
                        {appearance.wordCount || 0} words
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setViewingAppearances(null)} className="cancel-button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomAlert
        isOpen={alert?.isOpen}
        onClose={alert?.onClose}
        onConfirm={alert?.onConfirm}
        onCancel={alert?.onCancel}
        title={alert?.title}
        message={alert?.message}
        type={alert?.type}
        confirmText={alert?.confirmText}
        cancelText={alert?.cancelText}
        showCancel={alert?.showCancel}
      />
    </div>
  );
}

export default Speakers;
